import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Language } from '../types';
import WelcomeAnimation from './WelcomeAnimation';
import { Calculator } from 'lucide-react';

interface AuthProps {
  language: Language;
}

const Auth: React.FC<AuthProps> = ({ language }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isTeacher, setIsTeacher] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [lastRequestTime, setLastRequestTime] = useState<number | null>(null);
  const [cooldownDuration, setCooldownDuration] = useState(12000);
  const [disableButton, setDisableButton] = useState(false);
  const [cooldownMessage, setCooldownMessage] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    if (lastRequestTime) {
      setDisableButton(true);
      const timeLeft = Math.max(0, cooldownDuration - (Date.now() - lastRequestTime));
      
      if (timeLeft > 0) {
        const seconds = Math.ceil(timeLeft / 1000);
        setCooldownMessage(
          language === 'en' 
            ? `Please wait ${seconds} seconds before trying again`
            : `Veuillez patienter ${seconds} secondes avant de réessayer`
        );

        const timer = setTimeout(() => {
          setDisableButton(false);
          setCooldownMessage(null);
        }, timeLeft);

        return () => clearTimeout(timer);
      } else {
        setDisableButton(false);
        setCooldownMessage(null);
      }
    }
  }, [lastRequestTime, cooldownDuration, language]);

  const extractRetryAfter = (error: any): number => {
    const match = error.message?.match(/after (\d+) seconds/);
    if (match && match[1]) {
      return parseInt(match[1]) * 1000;
    }
    return 12000;
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      
      setError(
        language === 'en'
          ? 'Verification email sent. Please check your inbox and spam folder.'
          : 'Email de vérification envoyé. Veuillez vérifier votre boîte de réception et vos spams.'
      );
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(
        language === 'en'
          ? 'Failed to resend verification email. Please try again later.'
          : 'Échec de l\'envoi de l\'email de vérification. Veuillez réessayer plus tard.'
      );
    } finally {
      setResendingEmail(false);
      setLastRequestTime(Date.now());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (lastRequestTime && Date.now() - lastRequestTime < cooldownDuration) {
      return;
    }

    setLoading(true);
    setError(null);
    setShowVerificationMessage(false);

    try {
      if (mode === 'signup') {
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              role: isTeacher ? 'teacher' : 'student'
            }
          }
        });
        
        if (authError) throw authError;
        if (!authData.user) throw new Error('No user data returned');

        await new Promise(resolve => setTimeout(resolve, 1000));

        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            role: isTeacher ? 'teacher' : 'student',
            updated_at: new Date().toISOString(),
          })
          .eq('id', authData.user.id);

        if (profileError) throw profileError;
        
        setShowVerificationMessage(true);
        setError(
          language === 'en'
            ? 'Please check your email to verify your account before signing in.'
            : 'Veuillez vérifier votre email pour confirmer votre compte avant de vous connecter.'
        );
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          if (error.message.includes('email_not_confirmed')) {
            setShowVerificationMessage(true);
            throw new Error(
              language === 'en'
                ? 'Please verify your email address before signing in.'
                : 'Veuillez vérifier votre adresse email avant de vous connecter.'
            );
          }
          throw error;
        }
        setShowWelcome(true);
      }
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);

      if (err.message?.includes('rate_limit')) {
        const newCooldown = extractRetryAfter(err);
        setCooldownDuration(newCooldown);
        setError(
          language === 'en'
            ? 'Too many attempts. Please wait before trying again.'
            : 'Trop de tentatives. Veuillez patienter avant de réessayer.'
        );
      }
    } finally {
      setLoading(false);
      setLastRequestTime(Date.now());
    }
  };

  if (showWelcome) {
    return <WelcomeAnimation language={language} onComplete={() => setShowWelcome(false)} />;
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Calculator className="h-12 w-12 text-indigo-600 dark:text-indigo-400" />
          <span className="font-bold text-3xl text-indigo-700 dark:text-indigo-300">MathMaster</span>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 border border-slate-200 dark:border-slate-700">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6 text-center">
          {mode === 'signin' 
            ? (language === 'en' ? 'Sign In' : 'Connexion')
            : (language === 'en' ? 'Create Account' : 'Créer un compte')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === 'en' ? 'First Name' : 'Prénom'}
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                    text-slate-900 dark:text-slate-100 rounded-lg 
                    focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
                  required
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === 'en' ? 'Last Name' : 'Nom'}
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                    text-slate-900 dark:text-slate-100 rounded-lg 
                    focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="isTeacher"
                  type="checkbox"
                  checked={isTeacher}
                  onChange={(e) => setIsTeacher(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400 
                    border-slate-300 dark:border-slate-600 rounded"
                />
                <label htmlFor="isTeacher" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {language === 'en' ? 'I am a teacher' : 'Je suis un enseignant'}
                </label>
              </div>
            </>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {language === 'en' ? 'Email' : 'Email'}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                text-slate-900 dark:text-slate-100 rounded-lg 
                focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {language === 'en' ? 'Password' : 'Mot de passe'}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                text-slate-900 dark:text-slate-100 rounded-lg 
                focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
              required
            />
          </div>

          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>
          )}

          {cooldownMessage && (
            <div className="text-amber-600 dark:text-amber-400 text-sm text-center">{cooldownMessage}</div>
          )}

          {showVerificationMessage && (
            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail || disableButton}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm underline"
              >
                {resendingEmail
                  ? (language === 'en' ? 'Sending...' : 'Envoi en cours...')
                  : (language === 'en' ? 'Resend verification email' : 'Renvoyer l\'email de vérification')}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || disableButton}
            className="w-full bg-indigo-600 dark:bg-indigo-500 text-white py-2 px-4 rounded-lg 
              hover:bg-indigo-700 dark:hover:bg-indigo-600 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span>{language === 'en' ? 'Loading...' : 'Chargement...'}</span>
            ) : mode === 'signin' ? (
              <span>{language === 'en' ? 'Sign In' : 'Se connecter'}</span>
            ) : (
              <span>{language === 'en' ? 'Create Account' : 'Créer un compte'}</span>
            )}
          </button>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signin' ? 'signup' : 'signin');
                setError(null);
                setShowVerificationMessage(false);
              }}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm"
            >
              {mode === 'signin' 
                ? (language === 'en' ? 'Need an account? Sign up' : 'Besoin d\'un compte ? Inscrivez-vous')
                : (language === 'en' ? 'Already have an account? Sign in' : 'Déjà un compte ? Connectez-vous')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Auth;